import React from 'react';
import {
  IconTicket, IconAlertTriangle, IconCircleCheck, IconClock,
  IconArrowUp, IconMoodSad, IconMoodSmile, IconMoodEmpty
} from '@tabler/icons-react';

const SENTIMENT_MAP = {
  positive: { icon: IconMoodSmile, color: '#22C55E', bg: '#DCFCE7' },
  neutral: { icon: IconMoodEmpty, color: '#F59E0B', bg: '#FEF3C7' },
  negative: { icon: IconMoodSad, color: '#EF4444', bg: '#FEE2E2' },
};

const STATUS_MAP = {
  open: { color: '#F59E0B', bg: '#FEF3C7' },
  resolved: { color: '#22C55E', bg: '#DCFCE7' },
  escalated: { color: '#EF4444', bg: '#FEE2E2' },
};

export default function Tickets({ lang, t, industry, demoPhase, notify }) {
  const tickets = industry.tickets || [];
  const totalTickets = tickets.length;
  const resolvedCount = tickets.filter(t => t.status === 'resolved').length;
  const resolutionRate = totalTickets > 0 ? Math.round((resolvedCount / totalTickets) * 100) : 0;

  return (
    <div style={{ animation: 'fadeIn 0.3s ease' }}>
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: t.totalTickets, value: totalTickets, icon: IconTicket, color: '#5B2D8E' },
          { label: t.resolutionRate, value: resolutionRate + '%', icon: IconCircleCheck, color: '#22C55E' },
          { label: t.avgResponse, value: industry.analytics.responseTime, icon: IconClock, color: '#1B6B93' },
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} style={{
              background: '#fff', borderRadius: 12, padding: '20px', border: '1px solid #E5E7EB',
              display: 'flex', alignItems: 'center', gap: 16, animation: `slideUp 0.3s ease ${i * 0.08}s both`
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12, background: s.color + '15',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Icon size={24} color={s.color} stroke={1.8} />
              </div>
              <div>
                <div className="font-mono" style={{ fontSize: 28, fontWeight: 700 }}>{s.value}</div>
                <div style={{ fontSize: 13, color: '#6B7280' }}>{s.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pattern alert */}
      {industry.patternAlertAr && (
        <div style={{
          background: '#FEE2E2', borderRadius: 12, padding: '16px 20px', marginBottom: 24,
          border: '1px solid #FECACA', display: 'flex', alignItems: 'flex-start', gap: 12,
          animation: 'slideUp 0.3s ease 0.3s both'
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, background: '#EF444422',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <IconAlertTriangle size={18} color="#EF4444" stroke={2} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#DC2626', marginBottom: 4 }}>{t.patternDetected}</div>
            <div style={{ fontSize: 14, color: '#991B1B', lineHeight: 1.6 }}>
              {lang === 'ar' ? industry.patternAlertAr : industry.patternAlertEn}
            </div>
          </div>
          <button onClick={() => notify(lang === 'ar' ? 'تم إبلاغ المدير' : 'Manager notified')} style={{
            padding: '6px 14px', borderRadius: 6, border: '1px solid #EF4444',
            background: '#fff', color: '#EF4444', cursor: 'pointer', fontSize: 12,
            fontWeight: 600, flexShrink: 0, fontFamily: 'inherit'
          }}>
            {t.notifyManager}
          </button>
        </div>
      )}

      {/* Ticket grid */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden',
        animation: 'slideUp 0.3s ease 0.4s both'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
              {['ID', lang === 'ar' ? 'العميل' : 'Customer', lang === 'ar' ? 'المشكلة' : 'Issue',
                lang === 'ar' ? 'المشاعر' : 'Sentiment', lang === 'ar' ? 'الحالة' : 'Status',
                lang === 'ar' ? 'الوقت' : 'Time', ''].map((h, i) => (
                <th key={i} style={{
                  padding: '12px 16px', fontSize: 12, fontWeight: 600, color: '#6B7280',
                  textAlign: lang === 'ar' ? 'right' : 'left', background: '#F8F9FA'
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tickets.map((ticket, i) => {
              const sent = SENTIMENT_MAP[ticket.sentiment];
              const stat = STATUS_MAP[ticket.status];
              const SentIcon = sent.icon;
              return (
                <tr key={i} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <span className="font-mono" style={{ fontSize: 13, fontWeight: 600, color: '#5B2D8E' }}>
                      {ticket.id}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 500 }}>
                    {lang === 'ar' ? ticket.customerAr : ticket.customerEn}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151', maxWidth: 280 }}>
                    {lang === 'ar' ? ticket.issueAr : ticket.issueEn}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px',
                      borderRadius: 6, background: sent.bg, fontSize: 12, fontWeight: 500, color: sent.color
                    }}>
                      <SentIcon size={14} stroke={2} />
                      {t[ticket.sentiment]}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                      background: stat.bg, color: stat.color
                    }}>
                      {t[ticket.status]}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span className="font-mono" style={{ fontSize: 12, color: '#9CA3AF' }}>{ticket.time}</span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {ticket.status === 'open' && (
                      <button onClick={() => notify(lang === 'ar' ? 'تم تصعيد التذكرة' : 'Ticket escalated')} style={{
                        padding: '4px 10px', borderRadius: 6, border: '1px solid #EF4444',
                        background: '#fff', color: '#EF4444', cursor: 'pointer', fontSize: 11,
                        fontWeight: 600, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4
                      }}>
                        <IconArrowUp size={12} /> {t.escalate}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
